import React, { useState, useEffect } from "react";
import { Card, Button, Progress, Spin } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import API from "../../../../app/helpers/devApi";
import "./LeaveWidget.css";
import { shallowEqual, useSelector } from "react-redux";

function StatusBadge({ status }) {
  const className = `leave-widget__status-badge leave-widget__status-${(status || "").toLowerCase()}`;
  return <span className={className}>{status}</span>;
}

export function LeaveWidget() {
  const history = useHistory();
  const [balances, setBalances] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    auth,
  } = useSelector(
    ({ auth }) => ({
      auth: auth.coreModuleInfo,
    }),
    shallowEqual
  );
  const employeeId = auth?.employee_id;
  useEffect(() => {
    const fetchLeaveDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await API.get("/attendify/leave-policies/dashboard/"+employeeId);
        const data = response?.data?.data ?? {};
        setBalances(Array.isArray(data.balances) ? data.balances : []);
        setRecentApplications(Array.isArray(data.recent_applications) ? data.recent_applications : []);
      } catch (err) {
        setError(err?.message || "Failed to load leave data");
        setBalances([]);
        setRecentApplications([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaveDashboard();
  }, [employeeId]);

  if (loading && balances.length === 0 && recentApplications.length === 0) {
    return (
      <div className="leave-widget">
        <Card className="leave-widget__card">
          <div className="leave-widget__loading">
            <Spin tip="Loading leave details..." />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="leave-widget">
      <div className="leave-widget__card">
        <div className="leave-widget__header">
          <h3 className="leave-widget__title">
            <CalendarOutlined />
          </h3>
          <Button
            size="small"
            className="leave-widget__apply-btn"
            onClick={() => history.push("/leave/apply")}
          >
            <PlusOutlined />
            Apply
          </Button>
        </div>
        <div className="leave-widget__content">
          {error && <div className="leave-widget__error">{error}</div>}
          {/* Leave Balances - API: leave_title, total_days, enjoyed_days, balance_days */}
          <div className="leave-widget__balances">
            {balances.map((leave) => {
              const total = Number(leave.total_days) || 0;
              const available = Number(leave.balance_days) ?? 0;
              const percent = total > 0 ? Math.round((available / total) * 100) : 0;
              return (
                <div
                  key={leave.leave_title || leave.type}
                  className="leave-widget__balance-row"
                >
                  <div className="leave-widget__balance-header">
                    <span className="leave-widget__balance-type">
                      {leave.leave_title}
                    </span>
                    <span className="leave-widget__balance-days">
                      {available} / {total} days
                    </span>
                  </div>
                  <Progress
                    percent={percent}
                    showInfo={false}
                    className="leave-widget__progress"
                  />
                </div>
              );
            })}
          </div>

          {/* Recent Applications - API: leave_title, status, date_range, days_count */}
          <div className="leave-widget__recent-section">
            <h4 className="leave-widget__recent-title">Recent Applications</h4>
            <div className="leave-widget__recent-list">
              {recentApplications.length === 0 && !error ? (
                <p className="leave-widget__empty">No recent applications.</p>
              ) : (
                recentApplications.map((app, idx) => {
                  const days = parseFloat(app.days_count) || 0;
                  return (
                    <div
                      key={`${app.leave_title}-${app.date_range}-${idx}`}
                      className="leave-widget__recent-item"
                    >
                      <div className="leave-widget__recent-item-left">
                        <div className="leave-widget__recent-icon-wrap">
                          <ClockCircleOutlined />
                        </div>
                        <div className="leave-widget__recent-details">
                          <p className="leave-widget__recent-type">{app.leave_title}</p>
                          <p className="leave-widget__recent-dates">
                            {app.date_range} ({days} {days !== 1 ? "days" : "day"})
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={app.status} />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
